import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid/models'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IssueApi from '../../../../api/IssueApi'
import ConvertiblesApi from '../../../../api/ConvertiblesApi'
import WarrantApi from '../../../../api/WarrantApi'
import { PressReleaseApi } from '../../../../api/PressReleasesApi'
import { formatDateYYYYMMDD, formatDateYYYYMMDDhhmmss } from '../../../../packages/utils/numberFormatters'
import { useAppLocale } from '../../../../i18n/localeUtils'
import { getPressReleaseLink } from '../../../../utils/get-press-release-link'
import { PressReleaseListItem } from '@finance-lab/utils/types'
import { PressReleasesFilter } from '../../../components/press-releases/PressReleasesFilter'
import {
    useFilterSettings,
    usePressReleaseSelectDialog,
    useResetPaginationOnFilterChange,
} from '../../../components/press-releases/utils'
import Box from '@mui/material/Box'

const issueApi = new IssueApi()
const convertibleApi = new ConvertiblesApi()
const warrantApi = new WarrantApi()
const pressReleaseApi = new PressReleaseApi()

const apiMapping = {
    issue: issueApi.findByIssueId.bind(issueApi),
    convertible: convertibleApi.findByConvertibleId.bind(convertibleApi),
    warrant: warrantApi.findByWarrantId.bind(warrantApi),
} as const

export const SelectPressReleaseDialog = ({
    defaultPressReleaseId,
    onClose,
}: {
    defaultPressReleaseId: string | null
    onClose: (pr: PressReleaseListItem | null) => void
}) => {
    const { convertibleId, issueId, warrantId } = useParams()
    const { language: locale } = useAppLocale()
    const { isOpen, close: closeDialog } = usePressReleaseSelectDialog()
    const { endDate, searchValue, contentType } = useFilterSettings()
    const [error, setError] = useState<Error | null>(null)
    const [companyId, setCompanyId] = useState<string | null>(null)
    const [selectedPressReleaseId, setSelectedPressReleaseId] = useState<string | null>(null)
    const [pressReleases, setPressReleases] = useState<PressReleaseListItem[] | null>(null)
    const [rowCount, setRowCount] = useState<number | null>(null)
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 30,
    })
    useResetPaginationOnFilterChange(setPaginationModel, { endDate, searchValue, contentType })
    const transactionType = convertibleId ? 'convertible' : issueId ? 'issue' : 'warrant'
    const transactionId = {
        convertible: convertibleId,
        issue: issueId,
        warrant: warrantId,
    }[transactionType] as string

    useEffect(() => {
        if (!isOpen) return

        const fetchEntity = async () => {
            const entity = await apiMapping[transactionType](transactionId)

            if (!entity) {
                setError(
                    new Error(
                        `Failed to retrieve result for transaction type ${transactionType} with id ${transactionId}`,
                    ),
                )
                return
            }

            const { companyId: companyIdFromEntity } = entity

            if (!companyIdFromEntity) {
                setError(
                    new Error(
                        `Failed to get companyId for transaction type ${transactionType} with id ${transactionId}`,
                    ),
                )
                return
            }

            setCompanyId(companyIdFromEntity)
        }

        setError(null)
        void fetchEntity()
    }, [isOpen, transactionId, transactionType])

    useEffect(() => {
        if (!isOpen || !companyId) return

        const fetchPressReleases = async () => {
            const result = await pressReleaseApi.allPressReleases(paginationModel, {
                companyId,
                maxPublicationDate: endDate ?? formatDateYYYYMMDD(new Date(), true),
                search: searchValue || undefined,
            })

            setPressReleases(result.data)
            setRowCount(result.totalCount)
        }

        void fetchPressReleases()
    }, [companyId, endDate, isOpen, paginationModel, searchValue])

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
    }, [endDate, searchValue])

    useEffect(() => {
        if (isOpen) {
            setSelectedPressReleaseId(defaultPressReleaseId)
        }
    }, [defaultPressReleaseId, isOpen])

    const columns: GridColDef[] = [
        {
            field: 'publishedAt',
            headerName: 'Publiceringsdatum',
            width: 200,
            valueFormatter(publishedAt) {
                return formatDateYYYYMMDDhhmmss(publishedAt, { locale })
            },
        },
        {
            field: 'title',
            headerName: 'Rubrik',
            flex: 1,
            renderCell({ row, formattedValue }) {
                if (!companyId) return formattedValue as string

                return (
                    <Link target='_blank' to={getPressReleaseLink(companyId, row.id)}>
                        {formattedValue}
                    </Link>
                )
            },
        },
        {
            field: 'body',
            headerName: 'Brödtext',
        },
    ]

    const handleSelect = ({ ids }: GridRowSelectionModel) => {
        const [id] = Array.from(ids)
        setSelectedPressReleaseId(id as string)
    }

    const handleCancel = () => {
        setSelectedPressReleaseId(null)
        closeDialog()
        onClose(null)
    }

    const handleClose = () => {
        closeDialog()
        onClose(pressReleases?.find((pr) => pr.id === selectedPressReleaseId) ?? null)
        setSelectedPressReleaseId(null)
    }

    return (
        <Dialog fullWidth maxWidth='lg' open={isOpen} onClose={handleCancel}>
            <DialogTitle sx={{ display: 'flex' }}>
                <span style={{ flexGrow: 1, alignSelf: 'center' }}>Koppla källa</span>
                <DialogActions
                    disableSpacing
                    sx={{ display: 'inline-flex', justifySelf: 'flex-end', alignSelf: 'flex-end' }}
                >
                    <Button disabled={!selectedPressReleaseId} variant='contained' onClick={handleClose}>
                        Välj
                    </Button>
                </DialogActions>
            </DialogTitle>

            <DialogContent>
                {error && <p>{error.toString()}</p>}

                <Box sx={{ marginBottom: 4 }}>
                    <PressReleasesFilter hasContentTypeSelector={false} isSingleDaySelect />
                </Box>
                <DataGridPro<PressReleaseListItem>
                    autoHeight
                    checkboxSelection
                    columnVisibilityModel={{ body: false }}
                    columns={columns}
                    disableColumnFilter
                    disableColumnSelector
                    disableMultipleRowSelection
                    initialState={{
                        filter: {
                            filterModel: {
                                items: [],
                                quickFilterExcludeHiddenColumns: false,
                            },
                        },
                    }}
                    isRowSelectable={({ row }) => !row.relatedTransactions.some((rt) => rt.id === transactionId)}
                    onPaginationModelChange={({ page, pageSize }) => {
                        setPaginationModel({ page, pageSize })
                    }}
                    onRowSelectionModelChange={handleSelect}
                    rows={pressReleases || []}
                    rowSelectionModel={{
                        type: 'include',
                        ids: new Set<string>([selectedPressReleaseId].filter((id) => !!id) as string[]),
                    }}
                    paginationModel={paginationModel}
                    paginationMode='server'
                    pagination
                    rowCount={rowCount ?? 0}
                ></DataGridPro>
            </DialogContent>
        </Dialog>
    )
}
